import React from 'react';
import { T } from '@transifex/react';
import { Button } from '@/components/ui/button';
import { useCompany } from 'src/app/Company.store';
import { useGetToken } from 'src/api/config';
import { getAuthPropertyId } from 'src/api/state';
import { buildEidRedirectUrl } from 'src/api/eidRedirect';
import { selectFtnUrls } from 'src/api/eidProviders';

/**
 * Suomi.fi (Finnish Trust Network / FTN) login surface.
 *
 * Rendered by UserStoreProvider when `company.login_method === 'suomifi'`
 * (also reachable via the dev-pill "Login method" override).
 *
 * The backend surfaces a per-provider authorize URL in
 * `company.authentication_urls` with `provider: 'suomifi'` — unlike the
 * Criipto-brokered Danish/Norwegian providers, this points at the platform's own
 * SAML endpoint (`https://<platform-host>/suomifi/saml/login`). The query
 * contract is identical to MitID, so it shares `buildEidRedirectUrl`: the
 * anonymous session token goes out as `api_key`, and the IdP redirects back to
 * `redirect_url` with a fresh `?api_key=` that upgrades the session.
 *
 * The `api_key` is deliberately the *live* session token read at click time, not
 * a fixed credential — a hardcoded key would be a tenant secret in source and
 * would also go stale.
 *
 * Which provider values count as Suomi.fi lives in `selectFtnUrls`. When a
 * tenant has no Suomi.fi entry, this degrades to a disabled "coming soon"
 * placeholder rather than a dead button.
 *
 * TODO(branding): swap the plain button for the official Suomi.fi identification
 * button per FTN brand guidelines once the asset + broker onboarding are in place
 * (the broker's own method-picker handles bank/Mobiilivarmenne selection).
 */
export const SuomiFiLogin: React.FC = () => {
  const { data: company } = useCompany();
  const token = useGetToken();

  const ftnUrls = selectFtnUrls(company.authentication_urls);

  // Backend hasn't surfaced an FTN authorize URL yet (no Finnish tenant
  // configured) — render the placeholder rather than a dead button.
  if (ftnUrls.length === 0) {
    return (
      <div className="flex w-full flex-col gap-4">
        <Button type="button" disabled className="h-12 w-full">
          <T _str="Identify with Suomi.fi" />
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          <T _str="Suomi.fi sign-in is coming soon." />
        </p>
      </div>
    );
  }

  const redirect = (url: string) => {
    window.location.assign(
      buildEidRedirectUrl(url, token, getAuthPropertyId())
    );
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {ftnUrls.map((u) => (
        <Button
          key={u.url}
          type="button"
          onClick={() => redirect(u.url)}
          className="h-12 w-full"
        >
          <T _str="Identify with Suomi.fi" />
        </Button>
      ))}
    </div>
  );
};
